import { useLocation, useNavigate } from "react-router-dom";
import React, { use, useEffect, useState } from "react";
import { TextField, Grid, Box, Select, MenuItem, FormControl, Autocomplete } from "@mui/material";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import Checkbox from "@mui/material/Checkbox";
import MDButton from "components/MDButton";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import useTerritory from "hooks/useTerritory";
import useSales from "hooks/useSales";

const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
const checkedIcon = <CheckBoxIcon fontSize="small" />;

const TerritoryForm = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const territoryId = queryParams.get("id");
  const { createTerritory, getTerritoryById, updateTerritory, loading } = useTerritory();
  const { getSalesOptionList } = useSales();
  const [formData, setFormData] = useState({
    name: "",
    town: "",
    slug: "",
    sales: [],
  });
  const [sales, setSales] = useState([]);

  const handleSalesChange = (event, value) => {
    setFormData((prevState) => ({
      ...prevState,
      sales: value.map((sale) => sale._id),
    }));
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevState) => {
      let newState;

      if (name.includes(".")) {
        const keys = name.split(".");
        newState = {
          ...prevState,
          [keys[0]]: {
            ...prevState[keys[0]],
            [keys[1]]: value,
          },
        };
      } else {
        newState = {
          ...prevState,
          [name]: value,
        };
      }
      if (!territoryId && (name === "name" || name === "town")) {
        const updatedName = name === "name" ? value : prevState.name || "";
        const updatedTown = name === "town" ? value : prevState.town || "";
        const slug = `${updatedName.trim().toLowerCase().replace(/\s+/g, "-")}-${updatedTown
          .trim()
          .toLowerCase()
          .replace(/\s+/g, "-")}`;
        newState.slug = slug;
      }

      return newState;
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    // Validation for name and town
    if (!formData.name.trim()) {
      alert("Name is required and cannot be empty.");
      return;
    }

    if (!formData.town.trim()) {
      alert("Town is required and cannot be empty.");
      return;
    }

    if (formData.name.trim().length < 3) {
      alert("Name must be at least 3 characters long.");
      return;
    }

    if (formData.town.trim().length < 3) {
      alert("Town must be at least 3 characters long.");
      return;
    }
    if (territoryId) {
      const updateResposne = await updateTerritory(territoryId, formData);
      if (updateResposne?.success) {
        setFormData({
          name: "",
          town: "",
          slug: "",
          sales: [],
        });
        navigate("/territory-management");
      } else {
        alert(updateResposne?.message);
      }
      return;
    }
    const createResposne = await createTerritory(formData);
    if (createResposne?.success) {
      setFormData({
        name: "",
        town: "",
        slug: "",
        sales: [],
      });
      navigate("/territory-management");
    } else {
      alert(createResposne?.message);
    }
  };

  const fetchTerritory = async () => {
    const response = await getTerritoryById(territoryId);
    if (response.success) {
      setFormData({
        name: response.data.name,
        town: response.data.town,
        slug: response.data.slug,
        sales: response.data.sales.map((sale) => sale._id), // Assuming sales is an array of IDs
      });
    }
  };

  const fetchSalesList = async () => {
    const response = await getSalesOptionList();
    if (response.success) {
      setSales(response.data);
    }
  };

  useEffect(() => {
    fetchSalesList();
    if (territoryId) {
      fetchTerritory();
    }
  }, [territoryId]);

  return (
    <Box
      sx={{
        width: "100%",
        background: "white",
        minHeight: `calc(100vh - 120px)`,
        p: 4,
        borderRadius: 2,
        boxShadow: 2,
      }}
    >
      {/* Form Heading */}
      <MDTypography variant="h5" component="h1" align="center" gutterBottom>
        Territory Form
      </MDTypography>
      <MDTypography variant="caption" color="text" align="center" paragraph>
        Fill out the details below to register/update Territory.
      </MDTypography>

      {/* Form Fields */}
      <Grid container spacing={3}>
        {/* Name Field */}
        <Grid item xs={12} md={6}>
          <MDTypography variant="body2" gutterBottom>
            Name
          </MDTypography>
          <TextField
            fullWidth
            name="name"
            value={formData.name}
            onChange={handleChange}
            required
            variant="outlined"
          />
        </Grid>

        {/* Town Field */}
        <Grid item xs={12} md={6}>
          <MDTypography variant="body2" gutterBottom>
            Town
          </MDTypography>
          <TextField
            fullWidth
            name="town"
            value={formData.town}
            onChange={handleChange}
            required
            variant="outlined"
          />
        </Grid>

        {/* Slug Field */}
        <Grid item xs={12} md={6}>
          <MDTypography variant="body2" gutterBottom>
            Slug
          </MDTypography>
          <TextField
            fullWidth
            name="slug"
            value={formData.slug}
            // onChange={handleChange}
            disabled
            required
            variant="outlined"
          />
        </Grid>

        {/* Sales Dropdown */}
        <Grid item xs={12} md={6}>
          <MDTypography variant="body2" gutterBottom>
            Sales
          </MDTypography>
          <Autocomplete
            multiple
            name="sales"
            value={sales.filter((sale) => formData.sales.includes(sale._id))}
            onChange={handleSalesChange}
            id="checkboxes-tags-demo"
            options={sales}
            disableCloseOnSelect
            getOptionLabel={(option) => option?.userId?.name}
            renderOption={(props, option, { selected }) => {
              const { ...optionProps } = props;
              return (
                <li {...optionProps}>
                  <Checkbox
                    icon={icon}
                    checkedIcon={checkedIcon}
                    style={{ marginRight: 8 }}
                    checked={selected}
                  />
                  {option?.userId?.name}
                </li>
              );
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                sx={{
                  "& .MuiInputBase-root": {
                    height: 42, // Custom height
                    padding: "8px 14px",
                  },
                  "& .MuiInputBase-input": {
                    padding: "10px 0",
                  },
                }}
              />
            )}
          />
        </Grid>

        {/* Submit Button */}
        <Grid item xs={12}>
          <MDBox>
            <MDButton
              type="submit"
              variant="contained"
              color="dark"
              fullWidth
              onClick={handleSubmit}
              sx={{ marginTop: "3rem" }}
            >
              {loading ? "Submitting..." : territoryId ? "Update Territory" : "Submit"}
            </MDButton>
          </MDBox>
        </Grid>
      </Grid>
    </Box>
  );
};

export default TerritoryForm;
